import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  ChevronDown, Search, User, Building2, ArrowRight, Sparkles, ShieldCheck,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { buildFaqJsonLd } from '../lib/seo';

/* ─── Patient FAQ ────────────────────────────────────────────────────────── */
const PATIENT_FAQ = [
  {
    q: 'How does a treatment request work?',
    a: "You choose a treatment, submit a short request with your contact details and preferences, and our team reviews every request manually. We then match you with a selected trusted clinic partner who can support your case. The clinic — or our team on their behalf — contacts you with next steps, indicative pricing and availability.",
  },
  {
    q: 'Is VietClinics a clinic or hospital?',
    a: "No. VietClinics is a healthcare coordination platform. We are not a medical provider. Diagnosis, medical advice, treatment plans and final pricing are provided directly by the licensed clinics or doctors we match you with.",
  },
  {
    q: 'How are clinics selected for VietClinics?',
    a: "We only work with selected clinic partners who meet our standards for licensing, equipment, internationally trained specialists, hygiene, transparency and patient communication. We are not an open directory — clinics cannot simply list themselves on VietClinics.",
  },
  {
    q: 'What happens after I submit a request?',
    a: "Our team reviews your request, often within a few hours. We then match you with a suitable selected clinic partner. Our team or the clinic partner will contact you with the next steps — typically via WhatsApp or email — to discuss pricing guidance, availability and appointment options.",
  },
  {
    q: 'Do I pay VietClinics for treatment?',
    a: "No. You pay the clinic directly for your treatment. VietClinics does not charge patients for matching or coordination.",
  },
  {
    q: 'How does pricing work?',
    a: "Each treatment page shows indicative price ranges across our partner network. Final pricing is provided by the clinic after they review your case — usually after a virtual consultation or photos. We share pricing guidance up-front so there are no surprises.",
  },
  {
    q: 'Is treatment guaranteed once I submit a request?',
    a: "No. Submitting a request starts a conversation. The matched clinic will assess your case and confirm whether the treatment is suitable. If a clinic determines you are not a good candidate, we will tell you honestly — we never push treatments that aren't right for you.",
  },
  {
    q: 'Why are clinic names not always shown immediately?',
    a: "VietClinics matches each patient personally. Showing every clinic up-front creates confusion and the directory-style experience we deliberately avoid. The clinic that best fits your case is shared with you once we have reviewed your request.",
  },
  {
    q: 'How does VietClinics protect me from choosing the wrong clinic?',
    a: "Our team only matches you with partners who have proven track records for your specific treatment. We pre-screen the case fit, the clinic's specialty match and their international patient experience — so you don't have to compare ten directory listings yourself.",
  },
  {
    q: 'How are international patients supported?',
    a: "We offer English-speaking concierge support, help with visa letters, transport, hotel and recovery recommendations, and remote follow-up coordination. See our Travel Hub for the full international patient programme.",
  },
];

/* ─── Clinic Partner FAQ ─────────────────────────────────────────────────── */
const CLINIC_FAQ = [
  {
    q: 'Is VietClinics an open clinic directory?',
    a: "No. We are not a directory. We work with a curated network of selected clinic partners who can support our concierge process and meet our standards for quality and patient communication.",
  },
  {
    q: 'How does VietClinics work for clinics?',
    a: "Clinic partners receive qualified patient enquiries that have already been pre-screened by our team. Each enquiry includes the treatment, contact details, indicative budget and case background — so you spend less time on unqualified leads.",
  },
  {
    q: 'Is there a monthly fee for clinic partners?',
    a: "There is no monthly fee at the beginning unless specifically agreed. VietClinics may charge a platform service fee or an agreed partner fee structure depending on the volume and arrangement.",
  },
  {
    q: 'What is expected of clinic partners?',
    a: "Partners are expected to respond to qualified enquiries quickly and professionally, provide accurate treatment information and pricing guidance, communicate clearly with international patients, and uphold safety and hygiene standards.",
  },
  {
    q: 'How do I apply to become a clinic partner?',
    a: "Submit an enquiry through our For Clinics page or contact our partnership team directly. We will schedule a short meeting to understand your treatments, specialties and patient flow capacity, and walk you through the partnership process.",
  },
  {
    q: "Can I list my clinic's profile or specific doctors publicly?",
    a: "VietClinics keeps clinic identities confidential in the public-facing experience to preserve the concierge model. Clinic and doctor details are shared with patients once they have been matched. Some partners may be highlighted in our 'Selected Clinic Partner Network' trust strip on the homepage with logo and city only — no direct contact details.",
  },
];

const FAQPage = () => {
  const [query, setQuery] = useState('');
  const [tab, setTab] = useState('patient'); // 'patient' | 'clinic'

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const source = tab === 'patient' ? PATIENT_FAQ : CLINIC_FAQ;
    if (!q) return source;
    return source.filter(
      (item) =>
        item.q.toLowerCase().includes(q) ||
        item.a.toLowerCase().includes(q)
    );
  }, [query, tab]);

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>FAQ — How VietClinics Works | Patient &amp; Clinic Partner Questions</title>
        <meta name="description" content="Answers to common questions about VietClinics — how treatment requests work, how we select clinic partners, pricing, and international patient support." />
        <link rel="canonical" href="https://vietclinics.com/faq" />
        <script type="application/ld+json">{JSON.stringify(buildFaqJsonLd([...PATIENT_FAQ, ...CLINIC_FAQ]))}</script>
      </Helmet>
      <Header />

      {/* Hero */}
      <section className="relative overflow-hidden bg-white">
        <div aria-hidden className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(180deg, #F0FAF8 0%, #FFFFFF 60%)' }} />
        <div className="relative z-10 container mx-auto px-4 max-w-5xl py-16 lg:py-20 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-widest mb-5" style={{ background: '#DCEFEC', color: '#1A8F8A' }}>
            Help &amp; Frequently Asked
          </div>
          <h1 className="font-extrabold tracking-tight leading-[1.05] text-slate-900" style={{ fontSize: 'clamp(2.4rem, 4.6vw, 3.6rem)' }} data-testid="faq-title">
            Questions, answered.
          </h1>
          <p className="text-slate-600 text-lg mt-5 max-w-2xl mx-auto leading-relaxed">
            Everything you need to know about how VietClinics works — for patients and clinic partners.
          </p>

          {/* Search */}
          <div className="mt-8 max-w-xl mx-auto">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search questions…"
                className="w-full h-12 pl-11 pr-4 rounded-full bg-white border border-slate-200 text-slate-800 placeholder:text-slate-400 text-sm focus:outline-none focus:border-[#1FA7A1] focus:ring-2 focus:ring-[#1FA7A1]/15"
                data-testid="faq-search"
              />
            </div>
          </div>

          {/* Tabs */}
          <div className="mt-6 inline-flex bg-slate-100 rounded-full p-1" role="tablist">
            <button
              onClick={() => setTab('patient')}
              className={`flex items-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-full transition-all ${tab === 'patient' ? 'bg-white text-slate-900 shadow' : 'text-slate-500 hover:text-slate-700'}`}
              data-testid="faq-tab-patient"
            >
              <User className="h-4 w-4" /> For Patients
            </button>
            <button
              onClick={() => setTab('clinic')}
              className={`flex items-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-full transition-all ${tab === 'clinic' ? 'bg-white text-slate-900 shadow' : 'text-slate-500 hover:text-slate-700'}`}
              data-testid="faq-tab-clinic"
            >
              <Building2 className="h-4 w-4" /> For Clinic Partners
            </button>
          </div>
        </div>
      </section>

      {/* FAQ list */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4 max-w-3xl">
          {filtered.length === 0 ? (
            <p className="text-center text-slate-500 py-12">
              No questions match "{query}". <Link to="/contact" className="text-[#1FA7A1] font-semibold">Ask our team directly</Link>.
            </p>
          ) : (
            <div className="space-y-3">
              {filtered.map((item, i) => (
                <FaqItem key={`${tab}-${i}`} item={item} index={i} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Platform disclaimer */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4 max-w-3xl">
          <div className="bg-slate-50 rounded-2xl border border-slate-100 p-6 flex gap-3">
            <ShieldCheck className="h-5 w-5 text-[#1FA7A1] flex-shrink-0 mt-0.5" />
            <p className="text-sm text-slate-600 leading-relaxed">
              <strong className="text-slate-900">VietClinics is not a hospital or medical provider.</strong>{' '}
              We help patients connect with selected clinic partners. Final diagnosis, medical advice, treatment plans and pricing are provided directly by licensed clinics or doctors.
            </p>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-3xl text-center">
          <div className="rounded-[28px] px-8 py-12 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #0f2b55 0%, #1E5BA8 55%, #1FA7A1 100%)' }}>
            <p className="text-[11px] font-bold text-white/60 uppercase tracking-widest mb-3">Still have questions?</p>
            <h2 className="text-2xl md:text-3xl font-extrabold text-white tracking-tight mb-3">
              Our team usually replies within a few hours.
            </h2>
            <p className="text-white/80 text-sm mb-8 max-w-xl mx-auto">
              Submit a treatment request or ask us anything via the contact form.
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              <Link
                to="/#request-appointment"
                className="bg-white text-[#1FA7A1] font-bold rounded-full px-7 py-3.5 inline-flex items-center gap-2 hover:-translate-y-0.5 transition-all"
                data-testid="faq-cta-request"
              >
                Request Treatment <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/find-best-treatment"
                className="text-white font-semibold rounded-full px-7 py-3.5 border border-white/30 hover:bg-white/15 backdrop-blur-sm inline-flex items-center gap-2"
                data-testid="faq-cta-ai"
              >
                <Sparkles className="h-4 w-4" /> Talk to our AI Concierge
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

const FaqItem = ({ item, index }) => {
  const [open, setOpen] = useState(index === 0);
  return (
    <div className="bg-slate-50 rounded-[20px] border border-slate-100 overflow-hidden" data-testid={`faq-item-${index}`}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left"
      >
        <span className="font-bold text-slate-900 text-sm sm:text-base">{item.q}</span>
        <ChevronDown className={`h-5 w-5 text-slate-400 transition-transform flex-shrink-0 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-5 pb-5 -mt-1 text-slate-600 text-sm leading-relaxed">{item.a}</div>
      )}
    </div>
  );
};

export default FAQPage;
